import {
  AbsoluteAddressDropdown,
  AdminInput,
  AdminInputPlaceholder,
  AdminSpinner,
  Button,
  CreateManualDeliveryAddressData,
  Div,
  ErrorToast,
  LocationPinIcon,
  ModalPortal,
  api,
  lightTheme,
  showStringIfNotNull,
  reportError,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useState } from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
} from 'react-places-autocomplete';

// TODO MAKE COMPONENT AFTER TS MERGE BECAUSE ITS THE SAME AS ChangeLocationAddress

export const AddressInput = ({
  modalIsOpen,
  setModalIsOpen,
  setLoading,
  setConfirmAddressModal,
  title,
  inputLabel,
  placeholder,
}: {
  modalIsOpen: boolean;
  setModalIsOpen: Dispatch<SetStateAction<boolean>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setConfirmAddressModal: Dispatch<
    SetStateAction<CreateManualDeliveryAddressData | null>
  >;
  title: string;
  inputLabel?: string;
  placeholder?: string;
}) => {
  const [address, setAddress] = useState('');
  const [placeId, setPlaceId] = useState('');

  const finished = () => {
    setAddress('');
    setPlaceId('');
    setModalIsOpen(false);
  };

  async function getLocationDetails() {
    if (!address || !placeId)
      return ErrorToast('Please choose an address from the dropdown.');

    try {
      setLoading(true);
      const { data } = await api.get(
        `/v1/locations/${placeId}/parse-google-place`
      );

      setConfirmAddressModal(data.data);

      finished();
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalPortal
      isOpen={modalIsOpen}
      closeModal={() => setModalIsOpen(false)}
      logo={false}
      zIndex={135}
      header={title}
      overflow="visible"
      modalOverflow="visible"
    >
      <LocationSearchInput
        address={address}
        setAddress={setAddress}
        setPlaceId={setPlaceId}
        inputLabel={inputLabel}
        placeholder={placeholder}
      />

      <Button $mt="24" $w="100%" $selected onClick={getLocationDetails}>
        Confirm
      </Button>
      <Button $w="100%" $mt="16" $selected $gray onClick={finished}>
        Cancel
      </Button>
    </ModalPortal>
  );
};

export const ConfirmAddressInput = ({
  modalIsOpen,
  setModalIsOpen,
  handleConfirmAddress,
  title,
}: {
  modalIsOpen: CreateManualDeliveryAddressData | null;
  setModalIsOpen: Dispatch<
    SetStateAction<CreateManualDeliveryAddressData | null>
  >;
  handleConfirmAddress: (address: CreateManualDeliveryAddressData) => void;
  title: string;
}) => {
  if (modalIsOpen === null) return;

  const { address, city, country, state, zipCode, coordinates, streetNumber } =
    modalIsOpen;

  const handleInputChange = ({
    target: { name, value },
  }: {
    target: { name: string; value: string };
  }) => {
    setModalIsOpen((prevData) => ({
      ...(prevData as CreateManualDeliveryAddressData),
      [name]: value,
    }));
  };

  async function handleChangeAddress() {
    if (!address || !city || !country || !state || !zipCode || !streetNumber)
      return ErrorToast('Please fill in all information');

    handleConfirmAddress({
      address,
      city,
      country,
      state,
      zipCode,
      coordinates,
      streetNumber,
    });
  }

  const locationAddress = `${showStringIfNotNull(
    streetNumber
  )} ${showStringIfNotNull(address, true)} ${showStringIfNotNull(
    city,
    true
  )} ${showStringIfNotNull(zipCode, true)} ${showStringIfNotNull(
    state,
    true
  )} ${showStringIfNotNull(country)}`;

  return (
    <ModalPortal
      isOpen={modalIsOpen !== null}
      closeModal={() => setModalIsOpen(null)}
      logo={false}
      zIndex={140}
      header={title}
    >
      <Div $flex $column $gap="24px">
        <AdminInputPlaceholder
          value={address ? locationAddress : ''}
          $shadowDesign
          $textCenter
          Icn={LocationPinIcon}
        />

        <AdminInput
          $textCenter
          placeholder="Enter Here"
          label="Street Number"
          value={streetNumber ?? ''}
          name="streetNumber"
          onChange={handleInputChange}
        />

        <AdminInput
          $textCenter
          placeholder="Enter Here"
          label="Address"
          value={address ?? ''}
          name="address"
          onChange={handleInputChange}
        />

        <Div $flex $alignCenter $gap="16px" $width="100%">
          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="City"
            value={city ?? ''}
            name="city"
            onChange={handleInputChange}
          />

          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="Country"
            value={country ?? ''}
            name="country"
            onChange={handleInputChange}
          />
        </Div>
        <Div $flex $alignCenter $gap="16px" $width="100%">
          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="State"
            value={state ?? ''}
            name="state"
            onChange={handleInputChange}
          />

          <AdminInput
            $textCenter
            placeholder="Enter Here"
            label="Zip Code"
            value={zipCode ?? ''}
            name="zipCode"
            onChange={handleInputChange}
          />
        </Div>
      </Div>

      <Button $mt="24" $w="100%" $selected onClick={handleChangeAddress}>
        Confirm
      </Button>
      <Button
        $w="100%"
        $mt="16"
        $selected
        $gray
        onClick={() => setModalIsOpen(null)}
      >
        Cancel
      </Button>
    </ModalPortal>
  );
};

const LocationSearchInput = ({
  address,
  setAddress,
  setPlaceId,
  inputLabel = 'New Location Address',
  placeholder = 'Choose Address',
}: {
  address: string;
  setAddress: Dispatch<SetStateAction<string>>;
  setPlaceId: Dispatch<SetStateAction<string>>;
  inputLabel?: string;
  placeholder?: string;
}) => {
  const handleChange = (address: string) => {
    setAddress(address);
  };

  const handleSelect = async (address: string) => {
    setAddress(address);

    try {
      const results = await geocodeByAddress(address);
      if (results.length > 0) {
        const placeId = results[0].place_id;
        setPlaceId(placeId);
      }
    } catch (error) {
      console.error('Error fetching geocode results:', error);
    }
  };

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => {
        return (
          <Div $relative $mb="24">
            <AdminInput
              $textCenter
              {...getInputProps({
                label: inputLabel,
                placeholder: placeholder,
                // Chrome ignores the library's "off" on a field that looks
                // like an address and lays its saved addresses over the
                // Places suggestions; picking one skips the geocode. A
                // token it does not recognise turns that off.
                autoComplete: 'places-search',
              })}
            />
            {suggestions.length ? (
              <AbsoluteAddressDropdown $box $p="8px">
                {loading && <AdminSpinner absolute small />}
                {suggestions.map((suggestion, i) => {
                  const style = suggestion.active
                    ? { color: lightTheme.primary, cursor: 'pointer' }
                    : { backgroundColor: '', cursor: 'pointer' };
                  return (
                    <Div
                      {...getSuggestionItemProps(suggestion, {
                        style,
                      })}
                      $p="8px"
                    >
                      <span>{suggestion.description}</span>
                    </Div>
                  );
                })}
              </AbsoluteAddressDropdown>
            ) : null}
          </Div>
        );
      }}
    </PlacesAutocomplete>
  );
};
